import { routes } from '../../../common/contants/Routes';
import { isGuideDisabled } from '../helpers/guideDisableHelper';
import {
  applyHostColorSchemeToIframeElement,
  getHostColorScheme,
  startHostColorSchemeObserver
} from '../helpers/hostColorSchemeHelper';
import { MessageTypes } from '../types/MessageTypes';

export type BannerFrameLayout = {
  placement: 'TOP' | 'BOTTOM';
  dimensions: { width: string; height: string };
};

/**
 * Singleton iframe hosting the banner (`/bn/:companyId`).
 *
 * Sits just below the hotspot tooltip (2147483644) so every other GetDemo
 * surface paints over it. The banner overlays the page edge without pushing
 * content, and the iframe is sized to the banner itself so the rest of the
 * page stays clickable.
 *
 * Banners coexist with every other surface (guides, surveys, popups,
 * checklists): only the player decides whether one is on screen.
 */
class BannerContainer {
  private static instance: BannerContainer | null = null;
  private container: HTMLIFrameElement | null = null;
  private companyId: string | null = null;
  private fadeOutTimeout: ReturnType<typeof setTimeout> | null = null;
  private stopColorSchemeObserver: (() => void) | null = null;
  private constructor() {
    this.initialize();
  }

  public static getInstance(): BannerContainer {
    if (!BannerContainer.instance) {
      BannerContainer.instance = new BannerContainer();
    }
    return BannerContainer.instance;
  }

  private initialize(): void {
    const scriptElement = document.querySelector('script[gd-company-id]');
    this.companyId = scriptElement?.getAttribute('gd-company-id') || null;
    this.container = document.createElement('iframe');

    const baseUrl = import.meta.env.VITE_TOUR_GUIDE_FRONTEND_URL;
    const iframeUrl = new URL(`${baseUrl}${routes.BANNER_PLAYER}/${this.companyId}`);

    const parentParams = new URLSearchParams(window.location.search);
    if (parentParams.get('guideLog') === 'true') {
      iframeUrl.searchParams.set('guideLog', 'true');
    }
    if (isGuideDisabled()) {
      iframeUrl.searchParams.set('guideDisable', 'true');
    }
    const hostColorScheme = getHostColorScheme();
    iframeUrl.searchParams.set('hostColorScheme', hostColorScheme);

    this.container.src = iframeUrl.toString();
    applyHostColorSchemeToIframeElement(this.container, hostColorScheme);
    this.container.toggleAttribute('getdemo-banner-player', true);
    this.container.setAttribute('title', 'GetDemo banner');
    this.container.style.setProperty('position', 'fixed');
    this.container.style.setProperty('border', 'none');
    this.container.style.setProperty('background-color', 'transparent');
    this.container.style.setProperty('z-index', '2147483643');
    this.container.style.setProperty('width', '0');
    this.container.style.setProperty('height', '0');
    this.container.style.setProperty('top', '0');
    this.container.style.setProperty('left', '0');
    this.container.style.setProperty('opacity', '0');
    this.container.style.setProperty('display', 'none');
    this.container.style.setProperty('overflow', 'hidden');
    this.container.style.setProperty('transition', 'opacity 200ms ease');

    this.setupListeners();
    this.stopColorSchemeObserver = startHostColorSchemeObserver(scheme =>
      this.syncHostColorScheme(scheme)
    );
    document.documentElement.appendChild(this.container);
  }

  private syncHostColorScheme(hostColorScheme: string): void {
    applyHostColorSchemeToIframeElement(this.container, hostColorScheme);
    this.sendMessage({ type: MessageTypes.HOST_COLOR_SCHEME_CHANGED, hostColorScheme });
  }

  private setupListeners(): void {
    if (!this.container) return;

    this.container.addEventListener('load', () => {
      this.sendSavedUserData();
      this.syncHostColorScheme(getHostColorScheme());
    });
  }

  private sendSavedUserData(): void {
    const savedUserData = localStorage.getItem('getdemo_user');
    if (!savedUserData) return;
    try {
      this.sendMessage({
        type: MessageTypes.IDENTIFY_USER,
        userData: JSON.parse(savedUserData)
      });
    } catch (error) {
      console.error('Error processing saved user data:', error);
    }
  }

  public sendMessage(message: any): void {
    this.container?.contentWindow?.postMessage(message, '*');
  }

  public getContainer(): HTMLIFrameElement | null {
    return this.container;
  }

  public layout({ placement, dimensions }: BannerFrameLayout): void {
    if (!this.container) return;
    const style = this.container.style;
    const isFullWidth = dimensions.width === '100%';

    style.setProperty('width', dimensions.width);
    style.setProperty('height', dimensions.height);
    style.setProperty('max-width', '100vw');
    if (placement === 'BOTTOM') {
      style.setProperty('top', 'auto');
      style.setProperty('bottom', '0');
    } else {
      style.setProperty('top', '0');
      style.setProperty('bottom', 'auto');
    }
    style.setProperty('left', isFullWidth ? '0' : '50%');
    style.setProperty('transform', isFullWidth ? 'none' : 'translateX(-50%)');
  }

  public show(): void {
    this.setVisible(true);
  }

  public hide(): void {
    this.setVisible(false);
  }

  private setVisible(visible: boolean): void {
    if (!this.container) return;
    if (this.fadeOutTimeout) {
      clearTimeout(this.fadeOutTimeout);
      this.fadeOutTimeout = null;
    }

    if (visible) {
      this.container.style.setProperty('display', 'block');
      this.container.style.setProperty('opacity', '1');
      return;
    }

    this.container.style.setProperty('opacity', '0');
    this.fadeOutTimeout = setTimeout(() => {
      this.container?.style.setProperty('display', 'none');
      this.fadeOutTimeout = null;
    }, 200);
  }

  public destroy(): void {
    if (this.stopColorSchemeObserver) {
      this.stopColorSchemeObserver();
      this.stopColorSchemeObserver = null;
    }
    if (this.fadeOutTimeout) {
      clearTimeout(this.fadeOutTimeout);
      this.fadeOutTimeout = null;
    }
    if (this.container && document.documentElement.contains(this.container)) {
      document.documentElement.removeChild(this.container);
    }
    this.container = null;
    BannerContainer.instance = null;
  }

  public static destroy(): void {
    BannerContainer.instance?.destroy();
  }
}

export default BannerContainer;
